import React, { useState } from 'react';
import { channelLabel } from '../utils/channelInventory';

/**
 * @param {string} title         Optional. Overrides the default "Send {n} survey(s) to {m} recipients?" header.
 * @param {string} confirmLabel  Optional. Overrides the default "Send" button label.
 */
export default function SendConfirmModal({ recipientCount, linkedSurveys, recipients = [], onConfirm, onCancel, title, confirmLabel, stagingChannel = 'browser', dispatchMode = 'channel_response', preview = null }) {
    const [showRecipients, setShowRecipients] = useState(false);
    const normalizedRecipients = Array.isArray(recipients) ? recipients : [];
    const hasRecipientDetails = normalizedRecipients.length > 0;
    // Every context is an explicit channel now — web by default, never Auto —
    // so the whole batch travels one way and the old per-preference breakdown
    // ("email: 12 · sms: 3") would describe routing that no longer happens
    // (design §3.3). One plain sentence replaces it.
    const eligibleCount = preview?.eligible_count ?? recipientCount;
    const offeredChannels = [...new Set(preview?.offered_channels || [])].map(channelLabel);
    const channelOffer = offeredChannels.length
        ? `${new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(offeredChannels)} channel${offeredChannels.length === 1 ? '' : 's'}`
        : 'the eligible authored channels';
    const channelBreakdown = dispatchMode === 'channel_response'
        ? `Email ${eligibleCount} invitation${eligibleCount === 1 ? '' : 's'} offering ${channelOffer}.`
        : `Send ${eligibleCount} ${channelLabel(stagingChannel)} invitation${eligibleCount === 1 ? '' : 's'} directly.`;
    const survey = linkedSurveys[0] || null;

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={onCancel}>
            <div style={{
                background: '#fff', borderRadius: 8, padding: 24, width: 'min(520px, calc(100vw - 32px))',
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }} onClick={e => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 14px', fontSize: 16 }}>
                    {title || `Send “${preview?.survey_title || survey?.surveyName || 'survey'}” to ${eligibleCount} recipient${eligibleCount === 1 ? '' : 's'}?`}
                </h3>

                <table style={{
                    width: '100%', borderCollapse: 'collapse', marginBottom: 14,
                    fontSize: 13, color: '#3C4043',
                }}>
                    <thead>
                        <tr>
                            <th style={{
                                textAlign: 'left', borderBottom: '1px solid #E8EAED',
                                padding: '6px 4px', color: '#5F6368', fontWeight: 600,
                            }}>Survey</th>
                            <th style={{
                                textAlign: 'right', borderBottom: '1px solid #E8EAED',
                                padding: '6px 4px', color: '#5F6368', fontWeight: 600,
                            }}>Recipients</th>
                        </tr>
                    </thead>
                    <tbody>
                        {survey && (
                            <React.Fragment key={survey.deploymentId}>
                                <tr>
                                    <td style={{ padding: '7px 4px 2px', borderBottom: 'none' }}>
                                        {preview?.survey_title || survey.surveyName}
                                    </td>
                                    <td style={{
                                        padding: '7px 4px 2px', borderBottom: 'none',
                                        textAlign: 'right',
                                    }}>
                                        {eligibleCount}
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        colSpan={2}
                                        style={{
                                            padding: '0 4px 7px',
                                            borderBottom: '1px solid #F1F3F4',
                                            fontSize: 11,
                                            color: '#9AA0A6',
                                        }}
                                    >
                                        {channelBreakdown}
                                    </td>
                                </tr>
                            </React.Fragment>
                        )}
                    </tbody>
                </table>

                <button
                    type="button"
                    onClick={() => setShowRecipients(v => !v)}
                    style={{
                        padding: 0, border: 'none', background: 'transparent',
                        color: '#1967D2', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                        marginBottom: showRecipients ? 8 : 16,
                    }}
                >
                    {showRecipients ? 'Hide recipient list' : 'Show recipient list'}
                </button>
                {showRecipients && (
                    <div style={{
                        maxHeight: 160, overflowY: 'auto', border: '1px solid #E8EAED',
                        borderRadius: 4, marginBottom: 16,
                        background: '#fff', color: '#5F6368', fontSize: 13,
                    }}>
                        {hasRecipientDetails ? normalizedRecipients.map((recipient, index) => (
                            <div
                                key={recipient.id || index}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 8,
                                    padding: '4px 8px',
                                    background: index % 2 === 0 ? '#fff' : '#F8FAFD',
                                }}
                            >
                                <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                    {recipient.name || recipient.displayName || recipient.email || recipient.id}
                                    {recipient.email && (recipient.name || recipient.displayName) && recipient.email !== (recipient.name || recipient.displayName) && (
                                        <span> · {recipient.email}</span>
                                    )}
                                </span>
                            </div>
                        )) : (
                            <div style={{ padding: '4px 8px', background: '#fff' }}>
                                Recipient details unavailable.
                            </div>
                        )}
                    </div>
                )}

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={onConfirm} style={{
                        padding: '6px 16px', border: 'none', borderRadius: 4,
                        background: '#1967D2', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    }}>{confirmLabel || 'Send'}</button>
                    <button onClick={onCancel} style={{
                        padding: '6px 16px', border: '1px solid #DADCE0', borderRadius: 4,
                        background: '#fff', color: '#3C4043', cursor: 'pointer', fontSize: 13,
                    }}>Cancel</button>
                </div>
            </div>
        </div>
    );
}
